import BaseController from "./BaseController";
import Fragment from "sap/ui/core/Fragment";
import JSONModel from "sap/ui/model/json/JSONModel";
import MessageBox from "sap/m/MessageBox";
import MessageToast from "sap/m/MessageToast";
import type Dialog from "sap/m/Dialog";
import type TextArea from "sap/m/TextArea";
import type { Route$PatternMatchedEvent } from "sap/ui/core/routing/Route";
import type Context from "sap/ui/model/odata/v4/Context";
import type { Link$PressEvent } from "sap/m/Link";
import type { ResponseError } from "../types/Errors";
import { problemMessage } from "../util/ProblemDetails";

interface LicenseSummary {
	Id: string;
	DeploymentId: string;
	DeploymentName?: string;
	LicenseType: string;
	Quantity: number;
	Status: string;
	ValidFrom: string;
	ValidUntil: string;
}

interface ViewState {
	entitlementId: string;
	ownerId: string;
	ownerName: string;
	productId: string;
	productName: string;
	title: string;
	licenses: LicenseSummary[];
	kpi: {
		poolUsed: string;
		poolTotal: string;
	};
}

/**
 * @namespace computec.b2b.portal.controller
 */
export default class EntitlementDetail extends BaseController {
	private blockAutoExtendDialog?: Dialog;
	private extendGraceDialog?: Dialog;

	public override onInit(): void {
		const initial: ViewState = {
			entitlementId: "",
			ownerId: "",
			ownerName: "",
			productId: "",
			productName: "",
			title: "",
			licenses: [],
			kpi: { poolUsed: "0", poolTotal: "0" }
		};
		this.getView().setModel(new JSONModel(initial), "view");
		this.getRouter().getRoute("entitlementDetail")?.attachPatternMatched((e) => {
			this.onPatternMatched(e);
		});
	}

	private onPatternMatched(event: Route$PatternMatchedEvent): void {
		const args = event.getParameter("arguments") as { id?: string } | undefined;
		const id = args?.id ?? "";
		(this.getView().getModel("view") as JSONModel).setProperty("/entitlementId", id);

		this.getView().bindElement({
			path: `/Entitlements(${id})`,
			model: "odata"
		});

		void this.loadOwnerAndProduct(id);
		void this.loadLicenses(id);
	}

	private async loadOwnerAndProduct(entitlementId: string): Promise<void> {
		const view = this.getView().getModel("view") as JSONModel;
		const fetcher = this.getOwnerComponent().getAuthFetch();
		try {
			const ent = await fetcher.json<{ OwnerOrgId?: string; ProductId?: string; PurchasedQuantity?: number }>(
				`/odata/v4/Entitlements(${entitlementId})?$select=OwnerOrgId,ProductId,PurchasedQuantity`
			);
			if (ent.OwnerOrgId) {
				view.setProperty("/ownerId", ent.OwnerOrgId);
				try {
					const org = await fetcher.json<{ Name?: string }>(
						`/odata/v4/Organizations(${ent.OwnerOrgId})?$select=Name`
					);
					if (org.Name) {
						view.setProperty("/ownerName", org.Name);
					}
				} catch { /* silent */ }
			}
			if (ent.ProductId) {
				view.setProperty("/productId", ent.ProductId);
				try {
					const prod = await fetcher.json<{ Name?: string; Code?: string }>(
						`/odata/v4/Products(${ent.ProductId})?$select=Name,Code`
					);
					if (prod.Name) {
						view.setProperty("/productName", prod.Name);
						view.setProperty("/title", `${prod.Name} (${prod.Code ?? ""})`);
					}
				} catch { /* silent */ }
			}
			if (ent.PurchasedQuantity != null) {
				view.setProperty("/kpi/poolTotal", String(ent.PurchasedQuantity));
			}
		} catch {
			view.setProperty("/title", "—");
		}
	}

	private async loadLicenses(entitlementId: string): Promise<void> {
		const view = this.getView().getModel("view") as JSONModel;
		const fetcher = this.getOwnerComponent().getAuthFetch();
		try {
			const data = await fetcher.json<{ value: LicenseSummary[] }>(
				`/odata/v4/Licenses?$filter=EntitlementId%20eq%20${entitlementId}&$orderby=CreatedAt%20desc&$select=Id,DeploymentId,LicenseType,Quantity,Status,ValidFrom,ValidUntil`
			);
			const licenses = Array.isArray(data.value) ? data.value : [];

			await Promise.all(licenses.map(async (l) => {
				try {
					const dep = await fetcher.json<{ Name?: string }>(
						`/odata/v4/Deployments(${l.DeploymentId})?$select=Name`
					);
					l.DeploymentName = dep.Name ?? l.DeploymentId;
				} catch {
					l.DeploymentName = l.DeploymentId;
				}
			}));

			view.setProperty("/licenses", licenses);

			// Pool used = sum Quantity active licenses
			const used = licenses
				.filter(l => l.Status === "Active")
				.reduce((sum, l) => sum + (l.Quantity ?? 0), 0);
			view.setProperty("/kpi/poolUsed", String(used));
		} catch {
			view.setProperty("/licenses", []);
		}
	}

	public onNavBack(): void {
		this.navTo("entitlements", {});
	}

	public onRefresh(): void {
		const id = (this.getView().getModel("view") as JSONModel).getProperty("/entitlementId") as string;
		if (id) {
			this.getView().getModel("odata")?.refresh();
			void this.loadLicenses(id);
		}
	}

	public onOpenOwner(): void {
		const ownerId = (this.getView().getModel("view") as JSONModel).getProperty("/ownerId") as string;
		if (ownerId) {
			this.navToOrganization(ownerId);
		}
	}

	public onOpenProduct(): void {
		const productId = (this.getView().getModel("view") as JSONModel).getProperty("/productId") as string;
		if (productId) {
			this.navTo("productDetail", { id: productId });
		}
	}

	public onOpenDeployment(event: Link$PressEvent): void {
		const ctx = event.getSource().getBindingContext("view") as Context | null;
		if (!ctx) {
			return;
		}
		const id = ctx.getProperty("DeploymentId") as string | undefined;
		if (id) {
			this.navTo("deploymentDetail", { id });
		}
	}

	// === P2 — Block / Unblock auto-extend (Demo entitlements) ===

	/**
	 * Otwiera Dialog z TextArea na powód blokady. Pattern identyczny z MarkOrderOverdueDialog
	 * (TextArea, walidacja 1–500 na confirm + liveChange clear-state). Endpoint:
	 * <c>POST /api/v1/entitlements/{id}/block-auto-extend</c> z body <c>{ "Reason": string }</c>.
	 */
	public async onBlockAutoExtend(): Promise<void> {
		const entId = (this.getView().getModel("view") as JSONModel).getProperty("/entitlementId") as string;
		if (!entId) {
			return;
		}
		if (!this.blockAutoExtendDialog) {
			this.blockAutoExtendDialog = await Fragment.load({
				id: this.getView().getId(),
				name: "computec.b2b.portal.view.BlockAutoExtendDialog",
				controller: this
			}) as Dialog;
			this.getView().addDependent(this.blockAutoExtendDialog);
		}
		// Reset stanu — Fragment.load cache'uje instancję między openami.
		const ta = this.byId("taBlockAutoExtendReason") as TextArea;
		ta.setValue("");
		ta.setValueState("None");
		ta.setValueStateText("");
		this.blockAutoExtendDialog.open();
	}

	public onBlockAutoExtendReasonChange(): void {
		const ta = this.byId("taBlockAutoExtendReason") as TextArea;
		if (ta.getValue().trim().length > 0) {
			ta.setValueState("None");
			ta.setValueStateText("");
		}
	}

	public onCancelBlockAutoExtend(): void {
		this.blockAutoExtendDialog?.close();
	}

	public async onConfirmBlockAutoExtend(): Promise<void> {
		const entId = (this.getView().getModel("view") as JSONModel).getProperty("/entitlementId") as string;
		if (!this.blockAutoExtendDialog || !entId) {
			return;
		}
		const ta = this.byId("taBlockAutoExtendReason") as TextArea;
		const reason = ta.getValue().trim();
		const bundle = await this.getResourceBundle();

		if (reason.length === 0 || reason.length > 500) {
			ta.setValueState("Error");
			ta.setValueStateText(bundle.getText("entitlement.autoExtend.blockReasonRequired") ?? "Reason is required (1–500).");
			return;
		}

		try {
			const fetcher = this.getOwnerComponent().getAuthFetch();
			await fetcher.json(`/api/v1/entitlements/${entId}/block-auto-extend`, {
				method: "POST",
				body: JSON.stringify({ reason })
			});
			MessageToast.show(bundle.getText("entitlement.autoExtend.blockSuccess") ?? "Blocked");
			this.blockAutoExtendDialog.close();
			// Refresh OData entity — AutoExtendBlocked / AutoExtendBlockReason zmieniły się
			// po stronie backendu; binding czyta z cache OData v4 read-only feedu.
			this.getView().getModel("odata")?.refresh();
		} catch (e) {
			const err = e as ResponseError;
			MessageBox.error(problemMessage(err.bodyText, bundle.getText("entitlement.autoExtend.blockFailure") ?? "Failed"));
		}
	}

	/**
	 * Endpoint: <c>POST /api/v1/entitlements/{id}/unblock-auto-extend</c> bez body. Confirmation
	 * przez MessageBox — operacja jest reversibilna (można od razu zablokować ponownie), ale
	 * potwierdzenie chroni przed accidental clickiem. Permission entitlements.manage egzekwowany
	 * na endpoincie + widoczność buttonu w XML.
	 */
	public async onUnblockAutoExtend(): Promise<void> {
		const entId = (this.getView().getModel("view") as JSONModel).getProperty("/entitlementId") as string;
		if (!entId) {
			return;
		}
		const bundle = await this.getResourceBundle();
		try {
			const fetcher = this.getOwnerComponent().getAuthFetch();
			await fetcher.json(`/api/v1/entitlements/${entId}/unblock-auto-extend`, {
				method: "POST"
			});
			MessageToast.show(bundle.getText("entitlement.autoExtend.unblockSuccess") ?? "Unblocked");
			this.getView().getModel("odata")?.refresh();
		} catch (e) {
			const err = e as ResponseError;
			MessageBox.error(problemMessage(err.bodyText, bundle.getText("entitlement.autoExtend.unblockFailure") ?? "Failed"));
		}
	}

	// === ADR-025 — Activate + Extend grace (grace period enforcement) ===

	/**
	 * Aktywacja puli pod polityką ManualActivation. Confirmation przez MessageBox
	 * (operacja jednorazowa). Endpoint: <c>POST /api/v1/entitlements/{id}/activate</c>
	 * — bez logicznego body, wysyłamy "{}" żeby content-type/parser był zadowolony.
	 */
	public async onActivateEntitlement(): Promise<void> {
		const bundle = await this.getResourceBundle();
		MessageBox.confirm(bundle.getText("entitlement.action.activate.confirm") ?? "Activate?", {
			onClose: (action: unknown) => {
				if (action === MessageBox.Action.OK) {
					void this.doActivate();
				}
			}
		});
	}

	private async doActivate(): Promise<void> {
		const bundle = await this.getResourceBundle();
		const id = (this.getView().getModel("view") as JSONModel).getProperty("/entitlementId") as string;
		if (!id) {
			return;
		}
		try {
			const fetcher = this.getOwnerComponent().getAuthFetch();
			await fetcher.json(`/api/v1/entitlements/${id}/activate`, { method: "POST", body: "{}" });
			MessageToast.show(bundle.getText("entitlement.action.activate.successToast") ?? "Activated");
			this.getView().getModel("odata")?.refresh();
		} catch (e) {
			const err = e as ResponseError;
			MessageBox.error(problemMessage(err.bodyText, bundle.getText("entitlement.action.errorToast") ?? "Failed"));
		}
	}

	/**
	 * Otwiera Dialog Extend grace (StepInput dni 1–365 + TextArea reason). Fragment.load
	 * cache'uje instancję, więc przy ponownym otwarciu resetujemy model (days=7, reason="").
	 */
	public async onOpenExtendGraceDialog(): Promise<void> {
		const id = (this.getView().getModel("view") as JSONModel).getProperty("/entitlementId") as string;
		if (!id) {
			return;
		}
		if (!this.extendGraceDialog) {
			this.extendGraceDialog = await Fragment.load({
				id: this.getView().getId(),
				name: "computec.b2b.portal.view.ExtendGraceDialog",
				controller: this
			}) as Dialog;
			this.getView().addDependent(this.extendGraceDialog);
		}
		let m = this.getView().getModel("extendGrace") as JSONModel | undefined;
		if (!m) {
			m = new JSONModel({ days: 7, reason: "" });
			this.getView().setModel(m, "extendGrace");
		} else {
			m.setProperty("/days", 7);
			m.setProperty("/reason", "");
		}
		this.extendGraceDialog.open();
	}

	/**
	 * Endpoint: <c>POST /api/v1/entitlements/{id}/extend-grace</c> z body <c>{ days, reason }</c>.
	 * Reason walidowany min. 3 znaki (enabled buttona pilnuje tylko non-empty).
	 */
	public async onExtendGraceSubmit(): Promise<void> {
		const bundle = await this.getResourceBundle();
		const m = this.getView().getModel("extendGrace") as JSONModel;
		const days = Number(m.getProperty("/days"));
		const reason = String(m.getProperty("/reason") ?? "").trim();
		if (reason.length < 3) {
			return;
		}
		const id = (this.getView().getModel("view") as JSONModel).getProperty("/entitlementId") as string;
		if (!id) {
			return;
		}
		try {
			const fetcher = this.getOwnerComponent().getAuthFetch();
			await fetcher.json(`/api/v1/entitlements/${id}/extend-grace`, {
				method: "POST",
				body: JSON.stringify({ days, reason })
			});
			this.extendGraceDialog?.close();
			MessageToast.show(bundle.getText("entitlement.extendGrace.successToast") ?? "Extended");
			this.getView().getModel("odata")?.refresh();
		} catch (e) {
			const err = e as ResponseError;
			MessageBox.error(problemMessage(err.bodyText, bundle.getText("entitlement.extendGrace.errorToast") ?? "Failed"));
		}
	}

	public onExtendGraceCancel(): void {
		this.extendGraceDialog?.close();
	}

}
